import { Inbox } from 'lucide-react';
import { forwardRef, type HTMLAttributes, type ReactNode } from 'react';
import { cn } from '../lib/cn';

export interface EmptyStateProps extends Omit<HTMLAttributes<HTMLDivElement>, 'title'> {
  /** Icon inside the 40px tile (default inbox). */
  icon?: ReactNode;
  title: ReactNode;
  /** Supporting sentence (13px, secondary). */
  description?: ReactNode;
  /** Primary action, usually a <Button variant="secondary" size="compact">. */
  action?: ReactNode;
  /** Dashed border container (default true). */
  bordered?: boolean;
  /** Heading level of the title (default 3). */
  headingLevel?: 2 | 3 | 4;
}

/** "No feedback yet. When testers report something, it will appear here." + action. */
export const EmptyState = forwardRef<HTMLDivElement, EmptyStateProps>(function EmptyState(
  { icon, title, description, action, bordered = true, headingLevel = 3, className, children, ...rest },
  ref,
) {
  const Heading = `h${headingLevel}` as 'h2' | 'h3' | 'h4';
  return (
    <div
      ref={ref}
      className={cn(
        'flex flex-col items-center gap-2.5 px-4 py-6 text-center',
        bordered && 'rounded-md border border-dashed border-border',
        className,
      )}
      {...rest}
    >
      <span
        aria-hidden
        className="grid size-10 place-items-center rounded-[10px] bg-surface-2 text-text-3 [&>svg]:size-5"
      >
        {icon ?? <Inbox strokeWidth={1.8} />}
      </span>
      <div>
        <Heading className="m-0 text-14 font-semibold">{title}</Heading>
        {description ? <p className="m-0 mt-0.5 text-13 text-text-2">{description}</p> : null}
      </div>
      {action}
      {children}
    </div>
  );
});
